import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { getApiErrorMessage } from '../../utils/apiError';

const API_BASE_URL = import.meta.env.VITE_API_URL;

const getAuthHeaders = (getState: () => unknown) => {
  const state = getState() as { auth?: { token?: string | null } };
  const token = state.auth?.token;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const STATS_YEAR_MIN = 2025;
export const DEFAULT_STATS_CHART = 'students-flow';

export type StatsChartType = 'grouped-bar' | 'stacked-bar' | 'rate-bar' | 'consultations-breakdown' | 'line';
export type StatsUnit = 'count' | 'BYN' | 'per-100-usd' | 'USD';

export interface StatsSeriesPoint {
  month: string;
  value: number | null;
  /** Ads rates: the inverse cost (CPC/CAC); null when nothing converted. */
  cost?: number | null;
  spend?: number;
  conversions?: number;
  ads?: number;
}

export interface StatsRevenuePrediction {
  month: string;
  actualSoFar: number;
  predictedRemaining: number;
  predictedTotal: number;
  renewalsCount: number;
}

export interface StatsChartOptions {
  legend?: boolean;
  showNet?: boolean;
  prediction?: boolean;
  /** rate-bar: what each series counts, keyed by metric slug. */
  conversionLabels?: Record<string, string>;
  /** rate-bar: name of each series' inverse cost (CPC/CAC), keyed by metric slug. */
  costLabels?: Record<string, string>;
}

export interface StatsSourceCell {
  total: number;
  paid: number;
  /** Outcome not decided yet (scheduled / awaiting payment / waitlist). */
  open: number;
}

export interface StatsMetric {
  slug: string;
  label: string;
  seriesLabel: string;
  unit: StatsUnit;
  minYear: number;
  color: string;
  series: StatsSeriesPoint[];
  extras: {
    prediction?: StatsRevenuePrediction;
    /** Ad efficiency: the typical month. A month under this is marked red. */
    median?: number | null;
    /** Ad efficiency: the best month. Shown for comparison; it does not mark other months red. */
    best?: number | null;
    /** Consultations: month → status (or `none`) → count. */
    byStatus?: Record<string, Record<string, number>>;
    /** Consultations: month → source → counts; the FE derives the paid rate. */
    bySource?: Record<string, Record<string, StatsSourceCell>>;
    /** Ad spend: months after this one hold planned budget. */
    currentMonth?: string;
  } | null;
}

export interface StatsChartMeta {
  slug: string;
  label: string;
  selectorLabel: string;
  chartType: StatsChartType;
  metricSlugs: string[];
  options: StatsChartOptions;
}

export interface StatsChartView extends StatsChartMeta {
  metrics: StatsMetric[];
}

export interface StatsPayload {
  year: number;
  chart: StatsChartView;
  catalog: StatsChartMeta[];
  minYear: number;
}

interface StatsState {
  year: number;
  chartSlug: string;
  chart: StatsChartView | null;
  catalog: StatsChartMeta[];
  minYear: number;
  loading: boolean;
  error: string | null;
}

const initialState: StatsState = {
  year: Math.max(STATS_YEAR_MIN, new Date().getFullYear()),
  chartSlug: DEFAULT_STATS_CHART,
  chart: null,
  catalog: [],
  minYear: STATS_YEAR_MIN,
  loading: false,
  error: null,
};

export const fetchStatsAsync = createAsyncThunk(
  'stats/fetch',
  async ({ year, chart }: { year: number; chart?: string }, { rejectWithValue, getState }) => {
    try {
      const y = Math.max(STATS_YEAR_MIN, year);
      const slug = chart || DEFAULT_STATS_CHART;
      const response = await axios.get(`${API_BASE_URL}/stats`, {
        headers: getAuthHeaders(getState),
        params: { year: y, chart: slug },
      });
      const body = response.data as {
        data?: { year: number; chart: StatsChartView };
        meta?: { minYear?: number; charts?: StatsChartMeta[] };
      };
      return {
        year: body.data?.year ?? y,
        chart: body.data?.chart as StatsChartView,
        catalog: body.meta?.charts ?? [],
        minYear: body.meta?.minYear ?? STATS_YEAR_MIN,
      } satisfies StatsPayload;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch stats'));
    }
  },
);

const statsSlice = createSlice({
  name: 'stats',
  initialState,
  reducers: {
    setStatsYear: (state, action: PayloadAction<number>) => {
      state.year = Math.max(state.minYear, action.payload);
    },
    clearStatsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStatsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStatsAsync.fulfilled, (state, action: PayloadAction<StatsPayload>) => {
        state.loading = false;
        state.year = action.payload.year;
        state.chart = action.payload.chart;
        state.chartSlug = action.payload.chart.slug;
        state.catalog = action.payload.catalog;
        state.minYear = action.payload.minYear;
        state.error = null;
      })
      .addCase(fetchStatsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setStatsYear, clearStatsError } = statsSlice.actions;
export default statsSlice.reducer;
